<template>
  <div class="ak-range">
    <div v-if="showBubbles" class="ak-range__bubbles">
      <div class="ak-range__bubble" :style="lowValStyle">
        <output class="ak-range__bubble-inner" for="lowValue">
          {{ localValue }}
        </output>
      </div>
      <div v-if="multiple" class="ak-range__bubble" :style="highValStyle">
        <output class="ak-range__bubble-inner" for="highValue">
          {{ localHighValue }}
        </output>
      </div>
    </div>
    <div class="ak-range__inputs">
      <input
        v-model="sliderMin"
        type="range"
        :min="min"
        :max="max"
        :step="step"
        name="lowValue"
        class="ak-range__input"
        :style="backgroundStyle"
      />
      <input
        v-if="multiple"
        v-model="sliderMax"
        type="range"
        :min="min"
        :max="max"
        :step="step"
        name="highValue"
        class="ak-range__input"
        :style="backgroundStyle"
      />
      <div class="ak-range__input-track"></div>
    </div>
    <div class="ak-range__ticks">
      <span>{{ min }}</span>
      <span>{{ max }}</span>
    </div>
  </div>
</template>

<script>
import { defineComponent, ref, computed } from 'vue'

export default defineComponent({
  /**
   * @type {String}
   * @description This is the name of the component this will also
   * be the tag name of the component to use in templates
   * @example
   * <ak-range v-model:value="range"/>
   */

  name: 'AkRange',

  /**
   * @type {Object}
   * @description A collection of properties that may be passed into
   * the component these will be bound to the component instance and
   * may be access in the template and component methods.
   */
  props: {
    value: {
      type: Number,
      default: 0,
      required: true,
    },
    highValue: {
      type: Number,
      default: null,
    },
    multiple: {
      type: Boolean,
      default: false,
    },
    min: {
      type: Number,
      default: 0,
    },
    max: {
      type: Number,
      default: 100,
    },
    step: {
      type: Number,
      default: 1,
    },
    showBubbles: {
      type: Boolean,
      default: true,
    },
  },

  /**
   * @type {Array}
   * @description The list of events that this component can trigger
   */
  emits: ['update:value', 'update:highValue'],

  /**
   * @param {Object} props - The properties of the component
   * @param {Object} options - The setup options
   * @param {Function} options.emit - The emit method for triggering events
   * @description Sets up the component and returns an object containing all of the
   * attributes for the component to use.
   */
  setup(props, { emit }) {
    const localValue = ref(props.value)
    const localHighValue = ref(props.highValue)
    const gradStart = ref(props.min)
    const gradEnd = ref(props.max)

    const sliderMin = computed({
      get() {
        let val = parseInt(localValue.value)
        return val
      },
      set(val) {
        val = parseInt(val)
        if (props.multiple && val > localHighValue.value) {
          localHighValue.value = val
          emit('update:highValue', val)
        }
        localValue.value = val
        emit('update:value', val)
      },
    })

    const sliderMax = computed({
      get() {
        let val = parseInt(localHighValue.value)
        return val
      },
      set(val) {
        val = parseInt(val)
        if (val < localValue.value) {
          localValue.value = val
          emit('update:value', val)
        }
        localHighValue.value = val
        emit('update:highValue', val)
      },
    })

    const lowValPos = computed(() => {
      return (localValue.value - props.min) / (props.max - props.min)
    })

    const highValPos = computed(() => {
      return (localHighValue.value - props.min) / (props.max - props.min)
    })

    const gradient = computed(() => {
      if (props.multiple) {
        return (
          'linear-gradient(' +
          '90deg, ' +
          'hsl(233, 12%, 62%) 0%, ' +
          'hsl(233, 12%, 62%) ' +
          lowValPos.value * 100 +
          '%' +
          ', ' +
          'hsl(233, 94%, 59%) ' +
          lowValPos.value * 100 +
          '%' +
          ', ' +
          'hsl(233, 94%, 59%) ' +
          highValPos.value * 100 +
          '%' +
          ', ' +
          'hsl(233, 12%, 62%) ' +
          highValPos.value * 100 +
          '%' +
          ')'
        )
      }
      return (
        'linear-gradient(' +
        '90deg, ' +
        'hsl(233, 12%, 62%) 0%, ' +
        'hsl(233, 94%, 59%) 0%, ' +
        'hsl(233, 94%, 59%) ' +
        lowValPos.value * 100 +
        '%' +
        ', ' +
        'hsl(233, 12%, 62%) ' +
        lowValPos.value * 100 +
        '%' +
        ')'
      )
    })

    const lowValStyle = computed(() => {
      return {
        left: lowValPos.value * 100 + '%',
        marginLeft: 'calc(-23px * ' + lowValPos.value,
      }
    })

    const highValStyle = computed(() => {
      return {
        left: highValPos.value * 100 + '%',
        marginLeft: 'calc(-23px * ' + highValPos.value,
      }
    })

    const backgroundStyle = computed(() => {
      return { 'background-image': gradient.value }
    })

    return {
      localValue,
      localHighValue,
      gradStart,
      gradEnd,
      sliderMin,
      sliderMax,
      lowValPos,
      highValPos,
      gradient,
      lowValStyle,
      highValStyle,
      backgroundStyle,
    }
  },
})
</script>

<style lang="scss">
@import '../../scss/_settings.scss';
.ak-range {
  &__inputs {
    position: relative;
    height: 23px;
  }

  &__input {
    -webkit-appearance: none;
    position: absolute;
    left: 0;
    top: 8px;
    height: 8px;
    transition: 0.2s;
    -webkit-appearance: none;
    width: 100%;
    background-color: $color-grey-light;
    border-radius: 8px;

    &::-webkit-slider-runnable-track {
      width: 100%;
      height: 8px;
      cursor: pointer;
      transition: 0.2s;
      border-radius: 8px;
      box-shadow: none;
      border: 0;
    }
    &::-moz-range-track {
      width: 100%;
      height: 8px;
      cursor: pointer;
      transition: 0.2s;
      border-radius: 8px;
      box-shadow: none;
      border: 0;
    }
    &::-moz-range-thumb {
      z-index: 2;
      position: relative;
      box-shadow: 0px 0px 0px #000;
      border: 2px solid #fff;
      height: 23px;
      width: 23px;
      transition: $transition;
      border-radius: 23px;
      background: $color-primary;
      cursor: pointer;
      -webkit-appearance: none;
      margin-top: -8px;
    }
    &::-webkit-slider-thumb {
      z-index: 2;
      position: relative;
      box-shadow: 0px 0px 0px #000;
      border: 2px solid #fff;
      height: 23px;
      width: 23px;
      transition: $transition;
      border-radius: 23px;
      background: $color-primary;
      cursor: pointer;
      -webkit-appearance: none;
      margin-top: -8px;
    }

    &:focus {
      outline: none;
      &::-webkit-slider-thumb {
        box-shadow: $form-box-shadow-focus;
      }
    }

    &:active {
      &::-webkit-slider-thumb {
        transform: scale(1.25);
        box-shadow: none;
      }
    }

    &:hover {
      &::-webkit-slider-thumb {
        border-color: $color-primary;
      }
    }
  }

  &__bubbles {
    position: relative;
    height: 2rem;
  }
  &__bubble {
    position: absolute;
    bottom: 8px;
    width: 23px;
    display: flex;
    justify-content: center;
    white-space: nowrap;
    &::after {
      content: ' ';
      width: 0px;
      height: 0px;
      border-left: 5px solid transparent;
      border-right: 5px solid transparent;
      border-top: 5px solid $color-primary;
      position: absolute;
      top: 100%;
    }

    &-inner {
      padding: $spacing-tiny;
      font-size: $font-size-smaller;
      border-radius: $base-border-radius;
      background-color: $color-primary;
      font-weight: bold;
      line-height: 1;
      color: #fff;
    }
  }

  &__ticks {
    display: flex;
    justify-content: space-between;
    font-size: $font-size-small;
    line-height: 1.5;
  }
}

// IE11
input[type='range'].ak-range__input::-ms-track {
  width: 100%;
  height: 0.275em;
  cursor: pointer;
  /*remove bg colour from the track, we'll use ms-fill-lower and ms-fill-upper instead */
  background: transparent;

  /*leave room for the larger thumb to overflow with a transparent border */
  border-color: transparent;
  border-width: calc(1.25em / 2) 0;

  /*remove default tick marks*/
  color: transparent;
}
input[type='range'].ak-range__input::-ms-fill-lower {
  background: #2a6495;
}
input[type='range'].ak-range__input::-ms-fill-upper {
  background: $color-action;
}
input[type='range'].ak-range__input::-ms-thumb {
  box-shadow: 0 1px 3px 0 rgba(0, 0, 0, 0.5);
  height: 1.25em;
  width: 1.25em;
  border-radius: 50%;
  background: #fff;
  border: 2px solid $color-grey-light;
  cursor: pointer;
}
input[type='range'].ak-range__input:focus::-ms-fill-lower {
  background: $color-action;
}
input[type='range'].ak-range__input:focus::-ms-fill-upper {
  background: darken($color-action, 15%);
}
</style>
